import { create } from "zustand";

export const useNotesStore = create((set, get) => ({
  entityNotes: new Map(),

  getEntityState: (entityId) => {
    const { entityNotes } = get();
    return (
      entityNotes.get(entityId) || {
        notes: [],
        loading: false,
        activeNoteId: "NEW_NOTE",
        editingContent: "",
        pagination: null,
        hasMore: false,
        isInFocusedMode: false,
        archivedNotes: [],
      }
    );
  },
  setEntityNotesLoading: (entityId, loading) => {
    get().setEntityState(entityId, { loading });
  },
  // Helper function to set entity state
  setEntityState: (entityId, state) => {
    set((prevState) => {
      const newEntityNotes = new Map(prevState.entityNotes);
      newEntityNotes.set(entityId, { ...prevState.getEntityState(entityId), ...state });
      return { entityNotes: newEntityNotes };
    });
  },

  fetchNotes: async ({ appProperties, entityId, fetchSavedNotesByEntity, paginationParams = {} }) => {
    // Validate required parameters
    if (!appProperties?.osyncId || !entityId) {
      return;
    }

    const entityState = get().getEntityState(entityId);
    const { loading, isInFocusedMode } = entityState;

    // Prevent duplicate requests if already loading
    if (loading) {
      return;
    }

    // Set loading state to true
    get().setEntityState(entityId, { loading: true });

    try {
      // Fetch notes from the API
      const res = await fetchSavedNotesByEntity(entityId, paginationParams);
      const existingNotes = get().getNotesByEntityId(entityId);

      // Handle pagination: append new notes to existing ones, or replace all notes
      const newNotes = res?.data || [];
      let notesData = paginationParams.lastKey ? [...existingNotes, ...newNotes] : newNotes;

      // Remove duplicate notes by noteId to prevent issues
      const seenIds = new Set();

      notesData = notesData.filter((note) => {
        if (seenIds.has(note.noteId)) {
          return false;
        }
        seenIds.add(note.noteId);
        return true;
      });

      // Extract pagination info from response
      const paginationModel = res?.pagination || null;
      const hasMore = res?.hasMore || false;

      // Update the store with new data and set loading to false
      if (isInFocusedMode) {
        get().setEntityState(entityId, {
          archivedNotes: notesData,
          pagination: paginationModel,
          hasMore: hasMore,
          loading: false,
        });
      } else {
        get().setEntityState(entityId, {
          notes: notesData,
          pagination: paginationModel,
          hasMore: hasMore,
          loading: false,
        });
      }

      return { success: true, data: notesData };
    } catch (error) {
      console.error("Failed to fetch notes:", error);

      // Reset loading state on error
      get().setEntityState(entityId, { loading: false });
      return { success: false, error: error.message };
    }
  },

  // Set active note for a specific entity

  setActiveNoteId: (entityId, activeNoteId) => {
    get().setEntityState(entityId, { activeNoteId });
  },

  // Set editing content for a specific entity
  setEditingContent: (entityId, editingContent) => {
    get().setEntityState(entityId, { editingContent });
  },

  // Get active note ID for a specific entity
  getActiveNoteId: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.activeNoteId;
  },

  // Get editing content for a specific entity
  getEditingContent: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.editingContent;
  },

  // Add a new note to the list for a specific entity
  addNote: (entityId, note) => {
    const entityState = get().getEntityState(entityId);
    if (entityState.isInFocusedMode) {
      get().setEntityState(entityId, {
        archivedNotes: [note, ...entityState.archivedNotes],
      });
    } else {
      get().setEntityState(entityId, {
        notes: [note, ...entityState.notes],
      });
    }
    get().deactiveFocusNoteMode(entityId);
  },

  // Update an existing note in the list for a specific entity
  updateNote: (entityId, noteId, updatedNote) => {
    const entityState = get().getEntityState(entityId);
    if (entityState.isInFocusedMode) {
      get().setEntityState(entityId, {
        archivedNotes: entityState.archivedNotes.map((note) => (note.noteId === noteId ? { ...note, ...updatedNote } : note)),
      });
    } else {
      get().setEntityState(entityId, {
        notes: entityState.notes.map((note) => (note.noteId === noteId ? { ...note, ...updatedNote } : note)),
      });
    }
    get().deactiveFocusNoteMode(entityId);
  },

  // Delete a note from the list for a specific entity
  deleteNote: (entityId, noteId) => {
    const entityState = get().getEntityState(entityId);
    get().setEntityState(entityId, {
      notes: entityState.notes.filter((note) => note.noteId !== noteId),
      archivedNotes: entityState.archivedNotes.filter((note) => note.noteId !== noteId),
    });
    get().deactiveFocusNoteMode(entityId);
  },

  // Clear the store state for a specific entity
  clearEntityNotes: (entityId) => {
    set((state) => {
      const newEntityNotes = new Map(state.entityNotes);
      newEntityNotes.delete(entityId);
      return { entityNotes: newEntityNotes };
    });
  },

  // Clear all store state (useful for cleanup)
  clearStore: () => {
    set({
      entityNotes: new Map(),
    });
  },

  // Get notes for a specific entity
  getNotesByEntityId: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.notes;
  },

  // Get loading state for a specific entity
  getLoadingByEntityId: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.loading;
  },

  // Get pagination info for a specific entity
  getPaginationByEntityId: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.pagination || {};
  },

  // Get hasMore state for a specific entity
  getHasMoreByEntityId: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.hasMore;
  },

  activeFocusNoteMode: (entityId, note) => {
    const entityState = get().getEntityState(entityId);
    get().setEntityState(entityId, {
      notes: [note],
      isInFocusedMode: true,
      archivedNotes: entityState.notes,
    });
  },

  getIsInFocusedMode: (entityId) => {
    const entityState = get().getEntityState(entityId);
    return entityState.isInFocusedMode;
  },

  deactiveFocusNoteMode: (entityId) => {
    const entityState = get().getEntityState(entityId);
    if (entityState.isInFocusedMode) {
      if (entityState.archivedNotes) {
        get().setEntityState(entityId, {
          isInFocusedMode: false,
          notes: entityState.archivedNotes,
          archivedNotes: [],
        });
      } else {
        get().setEntityState(entityId, {
          isInFocusedMode: false,
        });
      }
    }
  },
}));
